/**
 * Flow Validation Utilities
 *
 * Client-side validation for flow structure to ensure flows are
 * agent-ready workflow packages.
 */

import type { FlowNode, FlowEdge } from "@/types/flow";

export interface FlowValidationIssue {
  code: string;
  severity: 'error' | 'warning' | 'info';
  nodeId?: string;
  nodeLabel?: string;
  message: string;
  resolution?: string;
}

export interface FlowStructureValidation {
  isValid: boolean;
  errors: FlowValidationIssue[];
  warnings: FlowValidationIssue[];
  hasGreetingNode: boolean;
  hasEndNode: boolean;
  orphanNodes: string[];
}

export interface RequiredAgentConfig {
  needsTransferNumber: boolean;
  needsHostEmail: boolean;
  needsCalendarConnection: boolean;
  needsKnowledgeBase: boolean;
}

/**
 * Get node type from node
 */
function getNodeType(node: FlowNode): string {
  if (node.type) return node.type;
  if (node.data?.type) return node.data.type;
  return 'unknown';
}

/**
 * Get node config from node
 */
function getNodeConfig(node: FlowNode): Record<string, unknown> {
  if (node.data?.config) return node.data.config as Record<string, unknown>;
  if (node.data) return node.data as Record<string, unknown>;
  return {};
}

/**
 * Get node label from node
 */
function getNodeLabel(node: FlowNode): string {
  const config = getNodeConfig(node);
  return (config.label as string) || (node.data?.label as string) || node.id;
}

/**
 * Find the entry/greeting node
 */
function findEntryNode(nodes: FlowNode[], edges: FlowEdge[]): FlowNode | null {
  // First, look for explicit greeting node
  const greetingNode = nodes.find(n => {
    const type = getNodeType(n);
    return type === 'greeting' || type === 'recordingConsent';
  });
  if (greetingNode) return greetingNode;

  // Otherwise, find the first message node that has no incoming edges
  const nodesWithIncoming = new Set(edges.map(e => e.target));
  const potentialEntryNodes = nodes.filter(n => {
    const type = getNodeType(n);
    return (type === 'message' || type === 'question') && !nodesWithIncoming.has(n.id);
  });

  if (potentialEntryNodes.length === 1) {
    return potentialEntryNodes[0];
  }

  // Look for any message node (fallback)
  return nodes.find(n => {
    const type = getNodeType(n);
    return type === 'message' || type === 'greeting';
  }) || null;
}

/**
 * Find all end nodes
 */
function findEndNodes(nodes: FlowNode[]): FlowNode[] {
  return nodes.filter(n => {
    const type = getNodeType(n);
    return type === 'end' || type === 'hangup';
  });
}

/**
 * Find orphan nodes (not connected to flow)
 */
function findOrphanNodes(nodes: FlowNode[], edges: FlowEdge[]): string[] {
  const entryNode = findEntryNode(nodes, edges);
  if (!entryNode) return nodes.map(n => n.id);

  // Build adjacency list
  const adjacency = new Map<string, string[]>();
  for (const node of nodes) {
    adjacency.set(node.id, []);
  }
  for (const edge of edges) {
    const sources = adjacency.get(edge.source);
    if (sources) sources.push(edge.target);
  }

  // BFS from entry node
  const visited = new Set<string>();
  const queue = [entryNode.id];
  visited.add(entryNode.id);

  while (queue.length > 0) {
    const current = queue.shift()!;
    const neighbors = adjacency.get(current) || [];
    for (const neighbor of neighbors) {
      if (!visited.has(neighbor)) {
        visited.add(neighbor);
        queue.push(neighbor);
      }
    }
  }

  // Filter out start nodes from orphan detection
  return nodes
    .filter(n => {
      const type = getNodeType(n);
      return !visited.has(n.id) && type !== 'start';
    })
    .map(n => n.id);
}

/**
 * Validate flow structure
 */
export function validateFlowStructure(
  nodes: FlowNode[],
  edges: FlowEdge[]
): FlowStructureValidation {
  const errors: FlowValidationIssue[] = [];
  const warnings: FlowValidationIssue[] = [];

  // Skip validation for empty flows
  if (!nodes || nodes.length === 0) {
    return {
      isValid: true,
      errors: [],
      warnings: [],
      hasGreetingNode: false,
      hasEndNode: false,
      orphanNodes: [],
    };
  }

  // Check for greeting/entry node
  const entryNode = findEntryNode(nodes, edges);
  const hasGreetingNode = !!entryNode;

  if (!hasGreetingNode && nodes.length > 0) {
    errors.push({
      code: 'MISSING_GREETING_NODE',
      severity: 'error',
      message: 'Flow must have a greeting or message node as the entry point',
      resolution: 'Add a Message or Greeting node as the first node in your flow',
    });
  }

  // Check for end node
  const endNodes = findEndNodes(nodes);
  const hasEndNode = endNodes.length > 0;

  if (!hasEndNode && nodes.length > 0) {
    errors.push({
      code: 'MISSING_END_NODE',
      severity: 'error',
      message: 'Flow must have at least one End node to properly terminate calls',
      resolution: 'Add an End node to ensure all conversation paths terminate properly',
    });
  }

  // Check for orphan nodes
  const orphanNodes = findOrphanNodes(nodes, edges);
  if (orphanNodes.length > 0) {
    for (const nodeId of orphanNodes) {
      const node = nodes.find(n => n.id === nodeId);
      if (node) {
        const nodeLabel = getNodeLabel(node);
        warnings.push({
          code: 'ORPHAN_NODE',
          severity: 'warning',
          nodeId,
          nodeLabel,
          message: `Node "${nodeLabel}" is not connected to the flow`,
          resolution: 'Connect this node to the flow or remove it',
        });
      }
    }
  }

  return {
    isValid: errors.length === 0,
    errors,
    warnings,
    hasGreetingNode,
    hasEndNode,
    orphanNodes,
  };
}

/**
 * Analyze required agent configuration from flow nodes
 */
export function analyzeRequiredAgentConfig(nodes: FlowNode[]): RequiredAgentConfig {
  let needsTransferNumber = false;
  let needsHostEmail = false;
  let needsCalendarConnection = false;
  let needsKnowledgeBase = false;

  for (const node of nodes) {
    const type = getNodeType(node);
    const config = getNodeConfig(node);

    // Check for transfer nodes
    if (type === 'transfer') {
      const phoneNumber = config.phoneNumber || config.phone_number || config.transferNumber;
      if (!phoneNumber || String(phoneNumber).includes('{{transfer_number}}')) {
        needsTransferNumber = true;
      }
    }

    // Check for appointment nodes
    if (type === 'appointment' || type === 'calcomBooking' || type === 'calBooking') {
      needsCalendarConnection = true;
      const hostEmail = config.hostEmail || config.host_email || config.calendarEmail;
      if (!hostEmail || String(hostEmail).includes('{{host_email}}')) {
        needsHostEmail = true;
      }
    }

    // Check for knowledge base nodes
    if (type === 'knowledgeBase' || type === 'rag' || type === 'knowledge') {
      needsKnowledgeBase = true;
    }

    // Check text fields for transfer/host placeholders
    const textFields = ['message', 'question', 'prompt'];
    for (const field of textFields) {
      const text = config[field];
      if (typeof text === 'string') {
        if (text.includes('{{transfer_number}}')) needsTransferNumber = true;
        if (text.includes('{{host_email}}')) needsHostEmail = true;
      }
    }
  }

  return {
    needsTransferNumber,
    needsHostEmail,
    needsCalendarConnection,
    needsKnowledgeBase,
  };
}

/**
 * Get validation status based on analysis
 */
export function getValidationStatus(
  structureValidation: FlowStructureValidation,
  requiredConfig: RequiredAgentConfig
): 'valid' | 'requires_config' | 'invalid' {
  if (!structureValidation.isValid) {
    return 'invalid';
  }

  if (
    requiredConfig.needsTransferNumber ||
    requiredConfig.needsHostEmail ||
    requiredConfig.needsCalendarConnection
  ) {
    return 'requires_config';
  }

  return 'valid';
}

/**
 * Remove duplicate edges from a flow.
 * Duplicates are identified by source + target + sourceHandle combination.
 * This prevents ElevenLabs from firing multiple nodes in sequence when
 * duplicate edges exist (e.g., from "Voicemail Outcome" node).
 *
 * @param edges - Array of flow edges
 * @returns Deduplicated array of edges
 */
export function deduplicateFlowEdges(edges: FlowEdge[]): FlowEdge[] {
  if (!edges || !Array.isArray(edges)) {
    return [];
  }

  const seen = new Set<string>();
  const unique: FlowEdge[] = [];

  for (const edge of edges) {
    // Create unique key from source + target + sourceHandle
    const key = `${edge.source}|${edge.target}|${edge.sourceHandle || 'default'}`;

    if (!seen.has(key)) {
      seen.add(key);
      unique.push(edge);
    }
  }

  const removed = edges.length - unique.length;
  if (removed > 0) {
    console.log(`⚠️ [Flow Validation] Removed ${removed} duplicate edge(s)`);
  }

  return unique;
}

/**
 * Add an edge to the edges array, preventing duplicates.
 * This should be used instead of React Flow's addEdge to prevent
 * duplicate edges from being created.
 *
 * @param newEdge - The edge to add
 * @param edges - Current array of edges
 * @returns Updated array with the new edge (if not duplicate)
 */
export function addEdgeWithDeduplication(newEdge: FlowEdge, edges: FlowEdge[]): FlowEdge[] {
  const key = `${newEdge.source}|${newEdge.target}|${newEdge.sourceHandle || 'default'}`;

  // Check if this edge already exists
  const exists = edges.some(edge => {
    const existingKey = `${edge.source}|${edge.target}|${edge.sourceHandle || 'default'}`;
    return existingKey === key;
  });

  if (exists) {
    console.log(`⚠️ [Flow Validation] Prevented duplicate edge: ${newEdge.source} -> ${newEdge.target}`);
    return edges; // Return unchanged
  }

  return [...edges, newEdge];
}
